import { useCallback, useEffect, useRef, useState } from 'react';
import { COSMETIC_BY_ID, effectivePrice, type Cosmetic } from '../../../shared/shop';
import { getClass } from '../../../shared/catalog/classes';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { audio } from '../game/audio';
import { formatDate, formatMoney, formatNumber, t, type Key } from '../i18n';
import { COSMETIC_DEF } from '../../../shared/cosmetics';
import { passItemLabel, shopName } from '../i18n/catalog';
import { Amount, CoinIcon, GemIcon, Icon, TokenIcon } from './icons';
import { LegalLink } from './LegalPage';
import { PageHead, type Page } from './MenuShell';
import { Modal } from './Panels';
import { CosmeticArt as RealCosmeticArt, ItemArt, RewardArt, itemKind, rewardLabel, s1ItemLabel, type ItemKind } from './rewards';
import { cosName } from './PaintPage';
import { usePassLevels } from './PassPage';
import { TankImage, TankStageView } from './TankPreview';
import { skinCodeOf } from './tankArt';

interface ShopData {
  week: string;
  resetAt: number;
  offers: { coins: string[]; gems: string[]; pack: string };
  cosmetics: Cosmetic[];
  tokenOffers: { id: string; token: string; count: number; gems: number }[];
  gemPacks: { id: string; name: string; chf: number; gems: number }[];
  conversions: { id: string; gems: number; coins: number }[];
  payments: { provider: 'stripe' | 'simulator' | 'closed'; testMode: boolean };
  inventory: string[];
  equipped?: string | null;
}

interface HistoryRow {
  currency: string;
  delta: number;
  reason: string;
  ref: string | null;
  created_at: string;
}

const rid = () => (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, '');
const chf = (c: number) => formatMoney(c, 'CHF');

/** Classes montrées dans l'aperçu d'une apparence : une palette s'applique à toutes les classes. */
const PREVIEW_CLASSES = ['BASE', 'C01-F', 'C06-A', 'C09-B'];
const SECTIONS = ['week', 'gems', 'tokens', 'convert', 'owned', 'history'] as const;
type Section = (typeof SECTIONS)[number];

const artKind = (c: Cosmetic): ItemKind => (c.kind === 'emblem' ? 'emblem' : c.kind === 'trail' ? 'trail' : c.kind === 'killfx' ? 'killfx' : c.kind === 'deathfx' ? 'deathfx' : c.kind === 'banner' ? 'banner' : c.kind === 'frame' ? 'frame' : 'item');

/** Vignette d'un cosmétique : le tank dans sa palette, ou le pictogramme de l'objet. */
function CosmeticArt({ c, size }: { c: Cosmetic; size: number }) {
  if (c.palette) return <TankImage cls={getClass('BASE')} look={{ code: skinCodeOf(c.id) }} size={size} />;
  return <ItemArt kind={artKind(c)} id={c.id} size={size} />;
}

/** Bouton de prix en deux temps : un premier clic arme l'achat, le second le confirme (4 s). Un double clic ne confirme
 *  pas : la confirmation n'est acceptée qu'un moment après l'armement, et le bouton reste inactif pendant l'achat. */
export function PriceButton({ price, disabled, onBuy, wide }: { price: { coins?: number; gems?: number }; disabled: boolean; onBuy: () => void | Promise<void>; wide?: boolean }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const armedAt = useRef(0);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);
  const amount = price.coins ? <Amount kind="coins" n={price.coins} /> : <Amount kind="gems" n={price.gems ?? 0} />;
  return (
    <button
      type="button"
      className={`btn btn-price ${armed ? 'armed' : ''} ${wide ? 'btn-wide' : ''}`}
      disabled={disabled || busy}
      onClick={(e) => {
        if (e.detail > 1) return; // deuxième clic d'un double clic
        if (armed) {
          if (performance.now() - armedAt.current < 500) return;
          setArmed(false);
          setBusy(true);
          void Promise.resolve(onBuy()).finally(() => setBusy(false));
        } else {
          armedAt.current = performance.now();
          setArmed(true);
        }
      }}
    >
      {armed ? <span className="price-confirm">{t('shopConfirm')}</span> : null}
      {amount}
    </button>
  );
}

/** Boutique : prix fixes, rotation hebdomadaire visible, statut possédé, aucun compte à rebours artificiel. */
export function ShopPage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  const [data, setData] = useState<ShopData | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [preview, setPreview] = useState<Cosmetic | null>(null);
  const [bought, setBought] = useState<string | null>(null);
  const [shake, setShake] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  // Noms des objets du pass, pour les afficher dans « Ma collection ».
  const passLevels = usePassLevels(me?.pass?.season.id);
  const [active, setActive] = useState<Section>('week');
  const account = me?.account ?? null;
  const verified = !!account?.verified;
  const sectionRefs = useRef<Partial<Record<Section, HTMLElement | null>>>({});

  const load = useCallback(async () => {
    try {
      setData(await apiGet<ShopData>('/api/shop'));
    } catch {
      /* nouvel essai au prochain affichage */
    }
  }, []);
  const loadHistory = useCallback(async () => {
    try {
      setHistory((await apiGet<{ history: HistoryRow[] }>('/api/history')).history ?? []);
    } catch {
      setHistory([]);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (account) void loadHistory();
  }, [account?.publicId, loadHistory]);

  // Sommaire : la section visible est mise en évidence.
  useEffect(() => {
    if (!data || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        const top = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (top) setActive((top.target as HTMLElement).dataset.section as Section);
      },
      { rootMargin: '-120px 0px -55% 0px' },
    );
    for (const el of Object.values(sectionRefs.current)) if (el) io.observe(el);
    return () => io.disconnect();
  }, [data]);

  if (!data) return <div className="page shop-page"><p className="wip">{t('loading')}</p></div>;

  const owned = new Set(data.inventory);
  const byId = new Map(data.cosmetics.map((c) => [c.id, c]));
  const equipped = data.equipped ?? null;
  const closed = data.payments.provider === 'closed';

  const act = async (path: string, body: Record<string, unknown>, cardId: string) => {
    const r = await apiPost<{ result?: string; ok?: boolean }>(path, { ...body, requestId: rid() });
    const result = r.data.result ?? (r.data.ok ? 'ok' : 'invalid');
    const ok = result === 'ok';
    setMsg({ text: t(`shop_${result}` as Key), ok });
    audio.play(ok ? 'uiBuy' : 'uiDeny');
    if (ok) {
      setBought(cardId);
      setTimeout(() => setBought((b) => (b === cardId ? null : b)), 1600);
    } else {
      setShake(cardId);
      setTimeout(() => setShake((s) => (s === cardId ? null : s)), 500);
    }
    await load();
    if (account) void loadHistory();
    onChanged();
  };
  const equip = async (itemId: string | null) => {
    const r = await apiPost<{ ok?: boolean }>('/api/shop/equip', { itemId });
    setMsg({ text: t(r.data.ok ? 'shopEquipped' : 'shop_invalid'), ok: !!r.data.ok });
    audio.play(r.data.ok ? 'uiClick' : 'uiDeny');
    await load();
    onChanged();
  };
  const buyPack = async (packId: string) => {
    const r = await apiPost<{ url?: string; error?: string }>('/api/checkout', { packId });
    if (r.data.url) location.href = r.data.url;
    else {
      setMsg({ text: t(`shop_${r.data.error ?? 'invalid'}` as Key), ok: false });
      audio.play('uiDeny');
    }
  };
  const jump = (s: Section) => {
    setActive(s);
    sectionRefs.current[s]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const sectionProps = (s: Section) => ({
    id: `shop-${s}`,
    'data-section': s,
    ref: (el: HTMLElement | null) => {
      sectionRefs.current[s] = el;
    },
  });

  const cosmeticCard = (c: Cosmetic, featured = false) => {
    // Pack : prix diminué de ce qui est déjà possédé ; plus en vente si tout son contenu l'est.
    const price = effectivePrice(c, owned);
    const has = owned.has(c.id) || price === null;
    const on = equipped === c.id;
    return (
      <article key={c.id} className={`shop-card tile cosmetic-card ${featured ? 'featured' : ''} ${has ? 'owned' : ''} ${bought === c.id ? 'bought' : ''} ${shake === c.id ? 'shake' : ''}`}>
        <button type="button" className="card-art" onClick={() => setPreview(c)} aria-label={t('shopPreviewOf', { name: shopName(c) })}>
          <CosmeticArt c={c} size={featured ? 132 : 96} />
          {c.palette && (
            <span className="swatch-strip" aria-hidden>
              {[c.palette.hull, c.palette.plate, c.palette.band, c.palette.barrel].map((n, i) => (
                <i key={i} style={{ background: `#${n.toString(16).padStart(6, '0')}` }} />
              ))}
            </span>
          )}
          <span className="card-zoom" aria-hidden>
            <Icon name="target" size={14} />
            {t('shopPreview')}
          </span>
        </button>
        <div className="card-body">
          <span className="eyebrow">{t(`ckind_${c.kind}` as Key)}</span>
          <strong className="card-name">{shopName(c)}</strong>
          {c.contains && (
            <span className="card-contains">
              {c.contains.map((x) => (byId.get(x) ? shopName(byId.get(x)!) : x)).join(' · ')}
            </span>
          )}
          <div className="card-foot">
            {has ? (
              c.palette && owned.has(c.id) ? (
                on ? (
                  <span className="badge badge-ok">
                    <Icon name="check" size={12} />
                    {t('shopEquippedBadge')}
                  </span>
                ) : (
                  <button type="button" className="btn btn-small" onClick={() => void equip(c.id)}>
                    {t('equip')}
                  </button>
                )
              ) : (
                <span className="badge badge-ok">
                  <Icon name="check" size={12} />
                  {t('owned')}
                </span>
              )
            ) : (
              <>
                {price && c.contains && price.gems !== c.price.gems && <span className="wip">{t('shopPackReduced')}</span>}
                <PriceButton price={price ?? c.price} disabled={!verified} onBuy={() => act('/api/shop/buy', { itemId: c.id }, c.id)} />
              </>
            )}
          </div>
        </div>
        {bought === c.id && (
          <span className="stamp display" aria-hidden>
            {t('shopBoughtStamp')}
          </span>
        )}
      </article>
    );
  };

  const weekly = [...data.offers.coins, ...data.offers.gems].map((id) => byId.get(id)).filter((c): c is Cosmetic => !!c);
  const pack = byId.get(data.offers.pack);
  const ownedSkins = data.inventory.map((id) => byId.get(id)).filter((c): c is Cosmetic => !!c?.palette);
  // Le reste de la collection : objets de boutique sans palette (emblèmes, traînées, effets), objets du pass, Singularité.
  // Noms : saison en cours (tableau chargé), objets S1 gardés à vie (tableau figé du catalogue), cosmétiques réels (cos_*).
  const passItems = new Map<string, string>();
  for (const l of passLevels ?? []) for (const r of [l.free, l.premium]) if (r.kind === 'item') passItems.set(r.item, rewardLabel(r));
  const ownedOthers = data.inventory
    .filter((id) => !byId.get(id)?.palette)
    .map((id) => {
      const c = byId.get(id);
      if (c) return { id, name: shopName(c), art: <CosmeticArt c={c} size={72} /> };
      // Le serveur range la Singularité sous « x:<variante> » (roulette.ts).
      if (id.startsWith('x:singularite')) return { id, name: t('singularityName'), art: <ItemArt kind="item" id={id} size={72} /> };
      const real = COSMETIC_DEF[id];
      if (real) return { id, name: cosName(id), art: <RealCosmeticArt d={real} size={72} /> };
      const s1 = s1ItemLabel(id);
      return { id, name: passItems.get(id) ?? (s1 !== undefined ? passItemLabel(id, s1) : id), art: <ItemArt kind={itemKind(id)} id={id} size={72} /> };
    });
  const providerText = closed ? t('shop_closed') : data.payments.provider === 'simulator' ? t('paySimulator') : data.payments.testMode ? t('payStripeTest') : t('payStripeLive');

  return (
    <div className="page shop-page">
      <PageHead title={t('nav_shop')} sub={t('shopSub')}>
        {me && (
          <div className="shop-balances">
            <Amount kind="coins" n={me.balances.coins ?? 0} size={20} />
            <Amount kind="gems" n={me.balances.gems ?? 0} size={20} />
          </div>
        )}
      </PageHead>

      <div className={`pay-banner ${data.payments.provider}`} role="note">
        <Icon name={closed ? 'lock' : 'alert'} size={18} />
        <span>{providerText}</span>
      </div>

      {!account ? (
        <div className="shop-gate">
          <p className="wip">{t('shopGuest')}</p>
          <button type="button" className="btn btn-small" onClick={() => go('profile')}>
            <Icon name="profile" size={16} />
            {t('register')}
          </button>
        </div>
      ) : (
        !verified && <p className="form-msg">{t('shopVerify')}</p>
      )}
      <div aria-live="polite">{msg && <p className={`form-msg ${msg.ok ? 'ok' : 'bad'}`}>{msg.text}</p>}</div>

      <nav className="shop-nav" aria-label={t('shopSections')}>
        {SECTIONS.filter((s) => s !== 'history' || account).map((s) => (
          <button key={s} type="button" className={active === s ? 'on' : ''} aria-current={active === s ? 'true' : undefined} onClick={() => jump(s)}>
            {t(`shopSec_${s}` as Key)}
          </button>
        ))}
      </nav>

      <section className="shop-section" {...sectionProps('week')} aria-labelledby="sec-week">
        <div className="section-head">
          <h2 className="plate-title" id="sec-week">
            {t('shopSec_week')}
          </h2>
          <span className="wip">{t('shopWeekNext', { d: formatDate(data.resetAt, 'datetime') })}</span>
        </div>
        <div className="shop-week">
          {pack && cosmeticCard(pack, true)}
          <div className="shop-grid">{weekly.map((c) => cosmeticCard(c))}</div>
        </div>
      </section>

      <section className="shop-section" {...sectionProps('gems')} aria-labelledby="sec-gems">
        <div className="section-head">
          <h2 className="plate-title" id="sec-gems">
            {t('gemPacks')}
          </h2>
          <span className="wip">
            {t('chfInfo')} <LegalLink page="terms" go={go} />
          </span>
        </div>
        <div className="shop-grid gem-grid">
          {data.gemPacks.map((p, i) => (
            <div key={p.id} className="shop-card gem-card" data-size={i + 1}>
              <RewardArt r={{ kind: 'gems', amount: p.gems }} size={72} />
              <strong className="card-name">{shopName(p)}</strong>
              <span className="gem-amount display num">
                <GemIcon size={18} />
                {formatNumber(p.gems)}
              </span>
              <button type="button" className="btn btn-primary btn-small" disabled={!verified || closed} onClick={() => void buyPack(p.id)}>
                {chf(p.chf)}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="shop-section" {...sectionProps('tokens')} aria-labelledby="sec-tokens">
        <div className="section-head">
          <h2 className="plate-title" id="sec-tokens">
            {t('tokensTitle')}
          </h2>
          <span className="wip">{t('tokensInfo')}</span>
        </div>
        <div className="shop-grid token-grid">
          {data.tokenOffers.map((o) => (
            <div key={o.id} className={`shop-card token-card ${bought === o.id ? 'bought' : ''} ${shake === o.id ? 'shake' : ''}`}>
              <TokenIcon token={o.token} size={44} />
              <div className="card-body">
                <strong className="card-name">
                  {t(`token_${o.token}` as Key)} {o.count > 1 && <span className="num">×{o.count}</span>}
                </strong>
                <span className="card-desc">{t(`tokdesc_${o.token}` as Key)}</span>
                {account && <span className="eyebrow num">{t('shopOwnedCount', { n: me?.balances[o.token] ?? 0 })}</span>}
              </div>
              <PriceButton price={{ gems: o.gems }} disabled={!verified} onBuy={() => act('/api/shop/tokens', { offerId: o.id }, o.id)} />
              {bought === o.id && (
                <span className="stamp display" aria-hidden>
                  {t('shopBoughtStamp')}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="shop-section" {...sectionProps('convert')} aria-labelledby="sec-convert">
        <div className="section-head">
          <h2 className="plate-title" id="sec-convert">
            {t('convertTitle')}
          </h2>
        </div>
        <div className="shop-grid convert-grid">
          {data.conversions.map((c) => (
            <div key={c.id} className={`shop-card convert-card ${bought === c.id ? 'bought' : ''} ${shake === c.id ? 'shake' : ''}`}>
              <span className="convert-line num">
                <GemIcon size={22} />
                <strong>{formatNumber(c.gems)}</strong>
                <Icon name="right" size={18} />
                <CoinIcon size={22} />
                <strong>{formatNumber(c.coins)}</strong>
              </span>
              <PriceButton price={{ gems: c.gems }} disabled={!verified} onBuy={() => act('/api/shop/convert', { lotId: c.id }, c.id)} />
            </div>
          ))}
        </div>
        <p className="wip">{t('convertInfo')}</p>
      </section>

      <section className="shop-section" {...sectionProps('owned')} aria-labelledby="sec-owned">
        <div className="section-head">
          <h2 className="plate-title" id="sec-owned">
            {t('collectionTitle')}
          </h2>
        </div>
        <div className="shop-grid">
          <div className={`shop-card owned-card ${equipped ? '' : 'on'}`}>
            <TankImage cls={getClass('C01-F')} look={{ code: 1 }} size={72} />
            <strong className="card-name">{t('variantsTitle')}</strong>
            <span className="card-desc">{t('variantsInTree')}</span>
            {equipped ? (
              <button type="button" className="btn btn-small" disabled={!account} onClick={() => void equip(null)}>
                {t('noCosmetic')}
              </button>
            ) : (
              <span className="badge badge-ok">
                <Icon name="check" size={12} />
                {t('shopEquippedBadge')}
              </span>
            )}
          </div>
          {ownedSkins.map((c) => (
            <div key={c.id} className={`shop-card owned-card ${equipped === c.id ? 'on' : ''}`}>
              <CosmeticArt c={c} size={72} />
              <strong className="card-name">{shopName(c)}</strong>
              {equipped === c.id ? (
                <span className="badge badge-ok">
                  <Icon name="check" size={12} />
                  {t('shopEquippedBadge')}
                </span>
              ) : (
                <button type="button" className="btn btn-small" onClick={() => void equip(c.id)}>
                  {t('equip')}
                </button>
              )}
            </div>
          ))}
          {ownedOthers.map((o) => (
            <div key={o.id} className="shop-card owned-card">
              {o.art}
              <strong className="card-name">{o.name}</strong>
              <span className="badge badge-ok">
                <Icon name="check" size={12} />
                {t('owned')}
              </span>
            </div>
          ))}
        </div>
      </section>

      {account && (
        <section className="shop-section" {...sectionProps('history')} aria-labelledby="sec-history">
          <div className="section-head">
            <h2 className="plate-title" id="sec-history">
              {t('shopSec_history')}
            </h2>
          </div>
          {!history ? (
            <p className="wip">{t('loading')}</p>
          ) : history.length === 0 ? (
            <p className="wip">{t('historyEmpty')}</p>
          ) : (
            <ol className="history-list">
              {history.map((h, i) => {
                const item = h.ref ? COSMETIC_BY_ID.get(h.ref) : undefined;
                const cur = h.currency === 'coins' || h.currency === 'gems' ? h.currency : null;
                return (
                  <li key={i}>
                    <span className="history-when num">{formatDate(new Date(h.created_at).getTime(), 'datetime')}</span>
                    <span className="history-what">
                      {t(`hist_${h.reason.replace('-', '')}` as Key)}
                      {item ? ` · ${shopName(item)}` : ''}
                    </span>
                    <span className={`history-delta num ${h.delta < 0 ? 'neg' : 'pos'}`}>
                      {h.delta > 0 ? '+' : '−'}
                      {cur ? <Amount kind={cur} n={Math.abs(h.delta)} size={14} /> : `${formatNumber(Math.abs(h.delta))} ${t(`token_${h.currency}` as Key)}`}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      )}

      {preview && (
        <ShopPreview
          item={preview}
          byId={byId}
          owned={owned.has(preview.id) || effectivePrice(preview, owned) === null}
          canEquip={owned.has(preview.id)}
          price={effectivePrice(preview, owned) ?? preview.price}
          equipped={equipped === preview.id}
          canBuy={verified}
          onClose={() => setPreview(null)}
          onBuy={() => act('/api/shop/buy', { itemId: preview.id }, preview.id)}
          onEquip={() => void equip(preview.id)}
        />
      )}
    </div>
  );
}

/** Aperçu complet avant achat : le tank animé dans la palette, sur plusieurs classes, et le contenu exact. */
function ShopPreview({
  item,
  byId,
  owned,
  equipped,
  canBuy,
  onClose,
  onBuy,
  onEquip,
  price,
  canEquip,
}: {
  item: Cosmetic;
  byId: Map<string, Cosmetic>;
  owned: boolean;
  price: Cosmetic['price'];
  canEquip: boolean;
  equipped: boolean;
  canBuy: boolean;
  onClose: () => void;
  onBuy: () => void;
  onEquip: () => void;
}) {
  const [clsId, setClsId] = useState(PREVIEW_CLASSES[0]);
  const cls = getClass(clsId);
  return (
    <Modal title={shopName(item)} onClose={onClose} wide>
      <div className="shop-preview">
        <div className="preview-stage">
          {item.palette ? (
            <>
              <TankStageView cls={cls} look={{ code: skinCodeOf(item.id) }} radius={58} label={t('shopPreviewOf', { name: shopName(item) })} />
              <div className="seg preview-classes" role="group" aria-label={t('shopPreviewClasses')}>
                {PREVIEW_CLASSES.map((id) => (
                  <button key={id} type="button" aria-pressed={clsId === id} onClick={() => setClsId(id)}>
                    <TankImage cls={getClass(id)} look={{ code: skinCodeOf(item.id) }} size={34} />
                  </button>
                ))}
              </div>
              <p className="wip">{matchMedia('(pointer: coarse)').matches ? t('hangarTapHint') : t('hangarFireHint')}</p>
            </>
          ) : (
            <ItemArt kind={artKind(item)} id={item.id} size={150} />
          )}
        </div>
        <div className="preview-info">
          <span className="eyebrow">{t(`ckind_${item.kind}` as Key)}</span>
          <p>{item.palette ? t('shopPaletteInfo') : t('shopItemInfo')}</p>
          {item.contains && (
            <ul className="preview-contains">
              {item.contains.map((x) => {
                const c = byId.get(x);
                return (
                  <li key={x}>
                    {c ? <CosmeticArt c={c} size={36} /> : null}
                    <span>{c ? shopName(c) : x}</span>
                    {c && <span className="wip">{t(`ckind_${c.kind}` as Key)}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="wip">{t('shopFairInfo')}</p>
          <div className="preview-actions">
            {owned ? (
              item.palette && canEquip ? (
                equipped ? (
                  <span className="badge badge-ok">
                    <Icon name="check" size={12} />
                    {t('shopEquippedBadge')}
                  </span>
                ) : (
                  <button type="button" className="btn" onClick={onEquip}>
                    {t('equip')}
                  </button>
                )
              ) : (
                <span className="badge badge-ok">
                  <Icon name="check" size={12} />
                  {t('owned')}
                </span>
              )
            ) : (
              <PriceButton price={price} disabled={!canBuy} onBuy={onBuy} wide />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** Suivi d'une commande au retour du paiement : le retour navigateur n'est pas une preuve. */
export function OrderWatcher({ orderId, onDone }: { orderId: string; onDone: (text: string) => void }) {
  useEffect(() => {
    let stop = false;
    let tries = 0;
    const tick = async () => {
      if (stop) return;
      const r = await apiGet<{ order: { status: string; gems: number } | null }>(`/api/orders/${orderId}`).catch(() => ({ order: null }));
      const st = r.order?.status;
      if (st === 'paid') return onDone(t('orderPaid', { n: r.order!.gems }));
      if (st === 'failed') return onDone(t('orderFailed'));
      if (++tries > 30) return onDone(t('orderPending'));
      setTimeout(tick, 1500);
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [orderId, onDone]);
  return null;
}
